import {
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

/**
 * Where this record came from, and who is responsible for it.
 *
 * Every row in the graph has carried this since LO-40 and nothing has ever
 * displayed it. That is the whole provenance argument sitting in a column
 * nobody reads.
 *
 * The part worth getting right is `mode`. It shipped with LO-55 as a positive
 * field precisely so that "nobody was there" stops being inferred from a null
 * `on_behalf_of` — a null that also means "a connector wrote it" and "the
 * username claim went missing". Rendering it as a raw string would put that
 * distinction straight back where it was.
 *
 * `agent` and `via` (LO-82) are two more nulls and they mean two different
 * things, which is the whole reason they are worded differently here rather
 * than both rendered as an em dash:
 *
 * - **No `agent` is a fact.** The app's own code wrote it, and there was no
 *   agent involved. Every write in this kernel's history is that row.
 * - **No `via` is an absence.** The kernel stamps it only from what its own
 *   edge can attest, and nothing attests one today — so "no transport
 *   recorded" is honest and "called directly" would be a claim nobody
 *   checked. That is the same overstatement the "say what a thing does not
 *   prove" rule is about, and it would be an easy one to make in a caption.
 *
 * ## A held field is the one case where the envelope is not the whole answer
 *
 * `held` (LO-97) names fields this org states directly over whatever the
 * source reported. The envelope above still describes the *record* — Google
 * synced it, at that time, through that action — and for a held field that
 * sentence is true about everything except the value on screen.
 *
 * So it is marked at the field rather than at the record. Leaving it out
 * would put Google's provenance next to a number Google never said, which is
 * precisely the invisible override the held row exists to replace.
 */

function Row({ label, children }) {
  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ color: "text.secondary", width: 130, border: 0, verticalAlign: "top" }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ border: 0 }}>{children}</TableCell>
    </TableRow>
  );
}

/**
 * A label/value block in its own scroll container.
 *
 * Three of these are on screen in a drawer that is `100%` wide at 390px, and
 * every value in the first two is a monospace id — a kernel id, a
 * `app:4uv8ip…` actor, an ISO timestamp. None of those wrap and together they
 * are wider than a phone. Before this they took the drawer sideways.
 *
 * `DataTable` is the same idea for a table with columns; this is the
 * two-column label/value shape, which has no header and a fixed first column,
 * so it is its own thing rather than a `DataTable` with the head hidden.
 */
function Facts({ children }) {
  return (
    <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
      <Table size="small">
        <TableBody>{children}</TableBody>
      </Table>
    </Box>
  );
}

function Mono({ children }) {
  return (
    <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
      {children}
    </Box>
  );
}

/** The one-sentence version, which is what somebody actually wants.
 *
 * Built as clauses rather than one template because `mode` is optional on old
 * rows, and folding "we do not know how" into the middle of the sentence
 * produced *"xero synced this, in a way this record does not say via
 * sync.person.upsert"* — grammatical, unreadable. Missing information belongs
 * after the sentence, not inside it.
 */
function Sentence({ p }) {
  const actor = (p.actor || "something").replace(/^(app|connector):/, "");
  const verb = p.actor?.startsWith("connector:") ? "synced" : "wrote";
  const who = p.on_behalf_of ? ` for ${p.on_behalf_of.replace(/^user:/, "")}` : "";
  // An agent is part of the actor, not a separate sentence: it belongs to the
  // app and acts inside that app's ceiling (LO-81), so "crowcase, agent
  // backlog-groomer, wrote this" is the honest shape and "the agent wrote
  // this" would suggest a principal that does not exist.
  const asAgent = p.agent ? (
    <>
      {", agent "}
      <Mono>{p.agent}</Mono>
      {","}
    </>
  ) : null;
  const how =
    p.mode === "interactive"
      ? ", with a person driving"
      : p.mode === "unattended"
        ? ", with nobody there"
        : "";

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2">
        <Mono>{actor}</Mono>
        {asAgent} {verb} this{who}
        {p.action ? (
          <>
            {" "}
            via <Mono>{p.action}</Mono>
          </>
        ) : null}
        {how}.
      </Typography>
      {!p.mode ? (
        <Typography variant="caption" color="text.secondary">
          Whether a person was involved is not recorded — this row predates the
          field.
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * @param row      a record from a collection, or an audit row (LO-83). Both
 *                 carry `provenance`, which is the block this component is
 *                 about; the two below are what only a record has.
 * @param fields   the type's field specs, from `GET /schema`. Omitted for an
 *                 audit row, which has no fields.
 * @param identity whether to render the record's own identity — id, source,
 *                 scope, tags. False for an audit row, which names a record
 *                 rather than holding one.
 *
 * The two optional halves exist so the Activity screen renders the *same*
 * envelope as the record pane instead of writing a second version of it.
 * Rendering them anyway against an audit row would have filled three tables
 * with em dashes, which reads as "this record has no source and no tags"
 * rather than "you are not looking at a record" — and a screen that worked
 * around that by keeping its own copy of this component is the drift the kit
 * exists to prevent.
 */
export default function Provenance({ row, fields, identity = true }) {
  const p = row.provenance || {};
  const specs = fields || [];

  const modeChip =
    p.mode === "interactive" ? (
      <Chip size="small" label="interactive" sx={{ color: "kernel.permitted", borderColor: "kernel.permitted" }} variant="outlined" />
    ) : p.mode === "unattended" ? (
      <Chip size="small" label="unattended" sx={{ color: "kernel.unattended", borderColor: "kernel.unattended" }} variant="outlined" />
    ) : (
      // Rows written before LO-55 have no mode. Saying so is more honest than
      // guessing, and it dates the record without needing a migration.
      <Typography variant="body2" color="text.secondary">
        not recorded — this row predates the field
      </Typography>
    );

  // Absent means "not an agent", never "an agent we could not identify"
  // (LO-82). Every write before the field existed was made by an app's own
  // code with no agent in the picture, so the null on those rows is true
  // rather than missing — which is why this reads as a statement and not as
  // the "predates the field" caption `mode` needs.
  const agentCell = p.agent ? (
    <Mono>{p.agent}</Mono>
  ) : (
    <Typography variant="body2" color="text.secondary">
      none — the app&apos;s own code, not an agent of it
    </Typography>
  );

  // And here absent means something different again, which is exactly why it
  // is not phrased like the row above. `via` is stamped by the kernel from
  // the authorizer context and nothing else; no attestation is not a claim
  // that the app called the kernel directly. Saying "direct" here would be
  // the overstatement the kit's own rules are about.
  const viaCell = p.via ? (
    <Chip
      size="small"
      variant="outlined"
      label={p.via}
      sx={{ fontFamily: "monospace" }}
    />
  ) : (
    <Typography variant="body2" color="text.secondary">
      no transport recorded — which is not a claim that this was a direct call
    </Typography>
  );

  return (
    <Stack spacing={1}>
      <Sentence p={p} />

      {/* A heading and the sentence under it. "Provenance" is the right
          word and it is not a word most people reading this screen use, and
          a block of eight labelled rows under a term somebody has to look up
          is a block they skip. */}
      <Typography variant="overline" color="text.secondary">
        Where this came from
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, maxWidth: "62ch" }}>
        Recorded by the kernel when the record was written, not supplied by
        whatever wrote it — so it cannot be left out or filled in with
        something else.
      </Typography>
      <Facts>
        <Row label="Written by">
          <Mono>{p.actor || "—"}</Mono>
        </Row>
        <Row label="For whom">
          {p.on_behalf_of ? (
            <Mono>{p.on_behalf_of}</Mono>
          ) : (
            <Typography variant="body2" color="text.secondary">
              nobody — not acting for a person
            </Typography>
          )}
        </Row>
        <Row label="Was a person there">{modeChip}</Row>
        <Row label="By an AI agent">{agentCell}</Row>
        <Row label="What was done">
          <Mono>{p.action || "—"}</Mono>
        </Row>
        <Row label="When">{p.at || "—"}</Row>
        <Row label="Where from">{p.source || "—"}</Row>
        <Row label="How it arrived">{viaCell}</Row>
      </Facts>

      {identity ? (
        <>
          <Divider sx={{ my: 1 }} />

          <Typography variant="overline" color="text.secondary">
            How it is identified
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, maxWidth: "62ch" }}>
            What this record is called here, what it was called in the system
            it came from, and the labels that decide who can see it.
          </Typography>
          <Facts>
            <Row label="Id here">
              <Mono>{row.id}</Mono>
            </Row>
            <Row label="Came from">
              <Mono>{row.source?.system || "—"}</Mono>
            </Row>
            <Row label="Id over there">
              <Mono>{row.source?.id || "—"}</Mono>
            </Row>
            <Row label="Shared with">
              <Mono>{row.scope || "—"}</Mono>
            </Row>
            <Row label="Labels">
              {(row.tags || []).length ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                  {row.tags.map((t) => (
                    <Chip key={t} size="small" label={t} variant="outlined" />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  untagged — visible to everyone who can read this type
                </Typography>
              )}
            </Row>
          </Facts>
        </>
      ) : null}

      {specs.length ? (
        <>
          <Divider sx={{ my: 1 }} />

          <Typography variant="overline" color="text.secondary">
            What it says
          </Typography>
          <Facts>
            {specs.map((f) => (
              <Row key={f.name} label={f.name}>
                {formatted(row, f)}
                <HeldNote held={row.held?.[f.name]} />
              </Row>
            ))}
          </Facts>
        </>
      ) : null}
    </Stack>
  );
}

/**
 * Who set this value, when the source did not (LO-97).
 *
 * Renders nothing at all for the overwhelming majority of fields, which is
 * why it is a note under the value rather than a column: a column would put
 * an em dash beside every field on every record to say "not corrected".
 *
 * Named in words, never by colour alone. "Set here" is the fact; the person
 * and the date are what somebody needs in order to ask about it.
 */
function HeldNote({ held }) {
  if (!held) return null;
  const who = (held.on_behalf_of || held.actor || "").replace(/^(user|app):/, "");
  const when = held.at ? new Date(held.at).toLocaleDateString() : "";
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      Set here{who ? ` by ${who}` : ""}{when ? ` on ${when}` : ""}, not by the
      source — and kept through every sync until somebody hands it back.
    </Typography>
  );
}

function formatted(row, field) {
  const value = row.fields?.[field.name];
  if (value === undefined || value === null || value === "") {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  if (Array.isArray(value)) return value.join(", ");
  if (field.minor_units && typeof value === "number") {
    return <Mono>{(value / 100).toFixed(2)}</Mono>;
  }
  return String(value);
}
